/**
 * AdminLayout — Layout wrapper for admin portal pages
 *
 * Supports two usage patterns:
 *   1. As a parent route element with nested `<Route>` children that render
 *      via `<Outlet />` (the existing `/admin/*` route tree).
 *   2. With an explicit `children` prop, mirroring `PortalLayout`'s API. This
 *      is used by the meridian-redesign flag-gated top-level routes
 *      (task 8.8 — `/admin/merit`, `/admin/envoy`, `/admin/patron`,
 *      `/admin/vault`) that mount as siblings of the existing `/admin` parent
 *      rather than as nested children of it.
 *
 * When `children` is provided it takes precedence over `<Outlet />`, matching
 * the `PortalLayout` pattern.
 *
 * Below the `md` breakpoint (< 768px) the layout switches to the
 * hamburger-drawer mobile pattern (PR (b2)) — see `PortalLayout`
 * for details. The two layouts share the same shape.
 */

import { useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import { MobileHeader } from './MobileHeader';
import { useIsBelowMd } from '@/lib/useMediaQuery';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { GlobalSearchProvider, SearchTrigger, GlobalSearchModal } from '@/components/search';

export interface AdminLayoutProps {
  children?: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps = {}) {
  const location = useLocation();
  const isMessagesPage = location.pathname.includes('/messages');
  const isBelowMd = useIsBelowMd();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  return (
    <GlobalSearchProvider>
    <div data-testid="admin-layout" className="flex h-screen bg-background overflow-hidden flex-col md:flex-row">
      {isBelowMd && (
        <MobileHeader onOpenSidebar={() => setMobileSidebarOpen(true)} />
      )}
      {isBelowMd && mobileSidebarOpen && (
        <div
          data-testid="admin-layout-backdrop"
          className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <AdminSidebar
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <main
        data-testid="admin-layout-content"
        className="admin-layout-main flex-grow p-4 sm:p-8 md:p-12 lg:p-20 overflow-y-auto overflow-x-hidden has-[[data-fullbleed=true]]:p-0 has-[[data-fullbleed=true]]:overflow-hidden relative min-w-0"
      >
        {!isMessagesPage && !isBelowMd && (
          <div className="absolute top-4 right-4 z-50 flex items-center gap-2">
            <NotificationBell />
            <SearchTrigger />
          </div>
        )}
        <GlobalSearchModal />
        {children || <Outlet />}
      </main>
    </div>
    </GlobalSearchProvider>
  );
}
